<script setup lang="ts">
import type { Order } from "@shopware-pwa/types";
import { computed } from "vue";
import { Divider } from "~/components/clarity-ui/Divider";
import { Tint } from "~/components/clarity-ui/Tint";
import useMarketSetup from "~/composables/useMarketSetup";
import { useOrderDetails } from "~/composables/shop/useOrderDetails";
import AccountOrderStateLabel from "~/templates/components/shop/account/AccountOrderStateLabel.vue";
import CommonLabel from "~/templates/components/CommonLabel.vue";
import OrderProductImage from "~/templates/components/shop/account/OrderProductImage.vue";
import SvgIcon from "~/templates/elements/SvgIcon.vue";
import {
  hasFailedPayment,
  ORDER_DETAILS_ASSOCIATIONS,
  orderArticleCount,
} from "~/utils/helper/shop/order";

export interface AccountOrderProps {
  order: Order;
}

const props = defineProps<AccountOrderProps>();

const { marketSetupAdmin, marketSetupLinks } = useMarketSetup();
const { order: refreshedOrder } = useOrderDetails(props.order.id, ORDER_DETAILS_ASSOCIATIONS);
const { push } = useRouter();

const gotoOrderDetails = async () =>
  await push(`${marketSetupAdmin.value?.myBritaOrderDetailsPage}/${props.order.id}`);

const gotoSubscriptions = async () =>
  await push(`${marketSetupAdmin.value?.myBritaSubscriptionPage}`);

const finalizePayment = async () =>
  await navigateTo(`${marketSetupLinks.value?.orderPaymentPage}/${props.order.id}`, {
    external: true,
  });

const order = computed(() => refreshedOrder.value ?? props.order);
const paymentCancelled = computed(() => hasFailedPayment(order.value));
const orderDate = computed(() =>
  new Date(order?.value?.orderDate).toLocaleDateString(
    (typeof navigator !== "undefined" && navigator.language) || "en-US",
  ),
);
const containsSubscriptionProduct = computed(
  () =>
    !!order.value.lineItems?.find((lineItem) =>
      lineItem?.children?.[0]?.payload?.hasOwnProperty("contractPlan"),
    ),
);
</script>

<template>
  <div
    class="flex flex-col gap-5 border-t pt-5 text-sm xl:gap-6 xl:rounded-lg xl:border xl:p-6 xl:pb-4"
    data-no-tracking
  >
    <div class="relative flex items-start gap-4">
      <OrderProductImage :line-item="order?.lineItems?.[0]" />

      <div class="grid w-full gap-2 xl:grid-cols-5 xl:gap-4">
        <div class="grid grid-cols-2 items-center gap-2 hyphens-auto xl:col-span-2 xl:gap-x-4">
          <AccountOrderStateLabel
            :state="order?.stateMachineState"
            class="col-span-full mb-1 place-self-start xl:col-span-1 xl:mb-0"
          />
          <CommonLabel
            class="font-medium xl:-order-1 xl:font-normal"
            path="BWF/shop/account/orderHistory/orderNumber"
          />
          <span class="self-start xl:text-2xl xl:font-medium">{{ order.orderNumber }}</span>
          <div class="contents xl:flex">
            <div class="font-medium xl:order-2 xl:font-normal">
              <CommonLabel
                v-if="orderArticleCount(order) > 1"
                path="BWF/shop/shared/articlePlural"
              />
              <CommonLabel v-else path="BWF/shop/shared/articleSingular" />
            </div>
            <div>{{ orderArticleCount(order) }}</div>
            <div class="hidden xl:block">&nbsp;</div>
          </div>
        </div>

        <button
          v-if="paymentCancelled && containsSubscriptionProduct"
          class="stretched-link text-link col-span-2 col-start-4 hidden place-self-start justify-self-end no-underline hover:underline xl:block"
          @click="gotoSubscriptions()"
        >
          <CommonLabel path="BWF/shop/account/orderHistory/finalizePayment" />
        </button>
        <button
          v-else-if="paymentCancelled"
          class="stretched-link text-link col-span-2 col-start-4 hidden place-self-start justify-self-end no-underline hover:underline xl:block"
          @click="finalizePayment()"
        >
          <CommonLabel path="BWF/shop/account/orderHistory/finalizePayment" />
        </button>
        <button
          v-else
          class="stretched-link text-link col-span-2 col-start-4 hidden place-self-start justify-self-end no-underline hover:underline xl:block"
          @click="gotoOrderDetails()"
        >
          <CommonLabel path="BWF/shop/account/orderHistory/orderDetails" />
        </button>

        <Divider class="col-span-full hidden xl:block" />

        <div class="hidden flex-col items-start gap-2 xl:flex">
          <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/dateOfOrder" />
          {{ orderDate }}
        </div>
        <div class="grid grid-cols-2 flex-col items-start gap-2 xl:flex">
          <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/status" />
          {{ order.deliveries?.[0]?.stateMachineState.name }}
        </div>
        <div class="hidden flex-col items-start gap-2 xl:flex">
          <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/paymentStatus" />
          <Tint v-if="paymentCancelled" color="failure">{{
            order.transactions?.[0]?.stateMachineState?.name
          }}</Tint>
          <template v-else>{{ order.transactions?.[0]?.stateMachineState?.name }}</template>
        </div>
        <div class="hidden flex-col items-start gap-2 xl:flex">
          <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/paymentMethod" />
          {{ order.transactions?.[0]?.paymentMethod?.name }}
        </div>
        <div class="hidden flex-col items-start gap-2 xl:flex">
          <CommonLabel class="font-medium" path="BWF/shop/account/orderHistory/shippingMethod" />
          {{ order.deliveries?.[0]?.shippingMethod?.name }}
        </div>
      </div>

      <div class="-mr-2 w-6 self-center xl:hidden">
        <button
          v-if="!paymentCancelled"
          class="stretched-link flex h-6 w-6 flex-shrink-0 items-center justify-center"
          @click="gotoOrderDetails()"
        >
          <SvgIcon class="h-4 w-4 -rotate-90" name="Arrow-Down" />
        </button>
      </div>
    </div>
    <button v-if="paymentCancelled" class="btn btn-blue xl:hidden" @click="finalizePayment()">
      <CommonLabel path="BWF/shop/account/orderHistory/finalizePayment" />
    </button>
  </div>
</template>
